import { css } from 'astroturf';
import { StaticImage } from 'gatsby-plugin-image';
import React, { Component } from 'react';
import BreakEven from '../components/BreakEven';
import CTA from '../components/CTA-newsletter';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import Link from '../components/link';
import Layout from '../layout';

const styles = css`
  .proof {
    margin: 0 auto;
    text-align: center;

    & > h2 {
      font-size: clamp(20px, 16px + 1.5vw, 26px);
      line-height: 1.25;
      color: #0a1530;
      font-weight: 800;
      margin: 0 auto 0.25rem;
      max-width: 32rem;
    }

    & > p {
      max-width: 34rem;
      margin: 0 auto 1.25rem;
      font-size: 0.9375rem;
      color: rgb(51, 51, 56);
    }
  }

  /* Local copies of the global .kpi/.kpilist look. Scoped so they can go one-per-row on
     narrow screens, which the global rule's 33% width will not do. */
  .kpilist {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    justify-content: center;
    gap: 1rem;
  }

  .kpi {
    border: 1px solid rgba(0, 70, 150, 0.1);
    border-radius: 1rem;
    padding: 1.5rem 1.25rem;
    text-align: center;
    line-height: 1.5;
    flex: 1 1 15rem;
    max-width: 18rem;
    min-width: min(15rem, 100%);

    & span {
      display: block;
      font-weight: 800;
      font-size: 1.5rem;
      line-height: 1.15;
      color: #0a1530;
      margin-bottom: 0.35rem;
      white-space: nowrap;
    }
  }

  .section {
    margin-top: 4rem;
    text-align: center;

    & > h2 {
      font-size: clamp(20px, 16px + 1.5vw, 26px);
      line-height: 1.25;
      color: #0a1530;
      font-weight: 800;
      margin: 0.25rem 0 0.25rem;
    }

    & > p {
      max-width: 34rem;
      margin: 0 auto;
      font-size: 0.9375rem;
      color: rgb(51, 51, 56);
    }
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2.5rem 1.5rem;
    margin-top: 2rem;
    text-align: left;

    @media (min-width: 34rem) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 52rem) {
      grid-template-columns: repeat(3, 1fr);
    }

    & > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      & svg {
        width: 32px;
        height: 32px;
        stroke: #2c82ad;
        margin: 0 0 0.6rem;
        display: block;
      }

      & h3 {
        margin: 0 0 0.35rem;
        font-size: 1.0625rem;
        line-height: 1.3;
        color: #0a1530;
        font-weight: 800;
      }

      & p {
        font-size: 0.9375rem;
        line-height: 1.6;
        color: rgb(51, 51, 56);
      }

      & > a {
        margin-top: 1rem;
      }
    }
  }

  .button {
    position: relative;
    outline: none;
    z-index: 2;
    border: 0px;
    height: 33px;
    line-height: 33px;
    border-radius: 40px;
    font-size: 0.875rem;
    color: rgb(255, 255, 255);
    font-weight: 700;
    white-space: nowrap;
    opacity: 1;
    padding: 0px;
    background: linear-gradient(135deg, rgb(44, 130, 173), rgb(103, 178, 111));
    margin-top: 0.75rem;
    display: inline-block;

    & i {
      display: inline-block;
      height: 33px;
      line-height: 33px;
      border-radius: 40px;
      padding: 0px 0.9rem;
      transition: background 0.25s ease-out 0s;
      background: rgb(10, 21, 48);
      font-style: normal;
    }

    &:is(:hover, :focus) i {
      background: transparent;
    }
  }
`;

class Page extends Component {
  constructor() {
    super();

    this.state = {
      currency: '€',
    };
  }

  componentDidMount() {
    if (window && window.Paddle) {
      window.Paddle.Product.Prices(599947, (prices) => {
        const isEUR = prices.price.gross.includes('€');
        this.setState({ currency: isEUR ? '€' : '$' });
      });
    }
  }

  render() {
    const { currency } = this.state;

    return (
      <Layout>
        <SEO
          title="Promotions and discounts to pay less"
          description="Polypane costs €0.45 per workday. See what it has to save you to be worth that, then find the discount that applies to you."
          keywords={['discount', 'discount code', 'promotion']}
          pathname={this.props.location.pathname}
        />
        <PageHeader>
          <StaticImage
            src="../../assets/images/icon.png"
            alt=""
            objectFit="contain"
            placeholder="none"
            width={150}
            formats={['PNG']}
          />
          <h1>Special offers</h1>
          <h2>Promotions and discounts to pay less or get Polypane for free.</h2>
        </PageHeader>

        <div className="page" style={{ maxWidth: '60rem' }}>
          <div className={styles.proof}>
            <BreakEven currency={currency} />

            <h2>Polypane saves you way more time and money than it costs</h2>
            <div className={styles.kpilist}>
              <div className={styles.kpi}>
                <span>3 to 4 hours</span>
                saved per project at{' '}
                <Link to="/blog/how-matchless-web-halved-their-mobile-optimization-time/">Matchless&nbsp;Web</Link>
              </div>
              <div className={styles.kpi}>
                <span>$700 a year</span>
                off the software bill at{' '}
                <Link to="/blog/how-short-hills-design-saves-over-700-per-year-thanks-to-polypane/">
                  Short&nbsp;Hills&nbsp;Design
                </Link>
              </div>
              <div className={styles.kpi}>
                <span>3 times</span>
                faster template builds at{' '}
                <Link to="/blog/how-red-pixel-themes-uses-polypane/">Red&nbsp;Pixel&nbsp;Themes</Link>
              </div>
            </div>
          </div>

          <p className={styles.section}>
            <Link to="/pricing/" track="offers-pricing" trackLabel="Go to pricing" className={styles.button}>
              <i>Go to pricing</i>
            </Link>
          </p>
          <div className={styles.section}>
            <h2>Ways to save</h2>
            <p>Getting Polypane can be more affordable.</p>

            <div className={styles.grid}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11.795 21h-6.795a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v4" />
                  <circle cx="18" cy="18" r="4" />
                  <path d="M15 3v4" />
                  <path d="M7 3v4" />
                  <path d="M3 11h16" />
                  <path d="M18 16.496v1.504l1 1" />
                </svg>
                <h3>Three months free</h3>
                <p>
                  Yearly is {currency}9 a month instead of {currency}12: nine months paid, twelve months used, every
                  year. The quiet weeks are covered by the busy ones.
                </p>
                <Link className={styles.button} to="/pricing/" track="offers-yearly" trackLabel="Get a yearly plan">
                  <i>Get a yearly plan</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="7" r="4" />
                  <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                </svg>
                <h3>Up to 40% off per seat</h3>
                <p>
                  Business plans get cheaper per seat as your team grows. Ten seats is up to 40% less per person, on one
                  invoice.
                </p>
                <Link className={styles.button} to="/pricing/" track="offers-business" trackLabel="Get a Business plan">
                  <i>Get a Business plan</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M8 9l5 5v7h-5v-4m0 4h-5v-7l5 -5m1 1v-6a1 1 0 0 1 1 -1h10a1 1 0 0 1 1 1v17h-8" />
                  <line x1="13" y1="7" x2="13" y2="7.01" />
                  <line x1="17" y1="7" x2="17" y2="7.01" />
                  <line x1="17" y1="11" x2="17" y2="11.01" />
                  <line x1="17" y1="15" x2="17" y2="15.01" />
                </svg>
                <h3>Let your employer pay</h3>
                <p>
                  Polypane costs less than most tools your team already pays for, and most employers are happy to cover
                  it. We wrote the email for you.
                </p>
                <Link
                  className={styles.button}
                  to="/dear-manager/"
                  track="offers-manager"
                  trackLabel="Get the example letter"
                >
                  <i>Get the example letter</i>
                </Link>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2>Qualifying offers</h2>
            <p>You might qualify for a discount if you meet certain criteria.</p>

            <div className={styles.grid}>
              <div>
                <svg
                  role="img"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <title>GitHub icon</title>
                  <path d="M9 19c-4.3 1.4 -4.3 -2.5 -6 -3m12 5v-3.5c0 -1 .1 -1.4 -.5 -2c2.8 -.3 5.5 -1.4 5.5 -6a4.6 4.6 0 0 0 -1.3 -3.2a4.2 4.2 0 0 0 -.1 -3.2s-1.1 -.3 -3.5 1.3a12.3 12.3 0 0 0 -6.2 0c-2.4 -1.6 -3.5 -1.3 -3.5 -1.3a4.2 4.2 0 0 0 -.1 3.2a4.6 4.6 0 0 0 -1.3 3.2c0 4.6 2.7 5.7 5.5 6c-.6 .6 -.6 1.2 -.5 2v3.5" />
                </svg>
                <h3>Free for a year for students</h3>
                <p>The GitHub Student Developer Pack includes a full year of Polypane, free.</p>
                <Link className={styles.button} to="/github-students/" track="offers-students" trackLabel="Sign up">
                  <i>Sign up</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6" />
                  <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4" />
                </svg>
                <h3>50% off for educators</h3>
                <p>Full-time educators get a yearly individual license at half price. Send proof of association.</p>
                <Link className={styles.button} to="/support/">
                  <i>Contact us</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19.5 13.572l-7.5 7.428l-7.5 -7.428m0 0a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
                </svg>
                <h3>50% off for charities</h3>
                <p>
                  Registered charities get a yearly license at half price, conditions apply. Send us your charitable
                  status.
                </p>
                <Link className={styles.button} to="/support/">
                  <i>Contact us</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 14c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3z" />
                  <path d="M9 14v4c0 1.656 2.686 3 6 3s6 -1.344 6 -3v-4" />
                  <path d="M3 6c0 1.072 1.144 2.062 3 2.598s4.144 .536 6 0c1.856 -.536 3 -1.526 3 -2.598c0 -1.072 -1.144 -2.062 -3 -2.598s-4.144 -.536 -6 0c-1.856 .536 -3 1.526 -3 2.598z" />
                  <path d="M3 6v10c0 .888 .772 1.45 2 2" />
                  <path d="M3 11c0 .888 .772 1.45 2 2" />
                </svg>
                <h3>Purchase power parity</h3>
                <p>
                  If your currency is under-valued against the Euro, the list price is not a fair price. Send proof of
                  your location.
                </p>
                <Link className={styles.button} to="/support/">
                  <i>Contact us</i>
                </Link>
              </div>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="3" y1="21" x2="21" y2="21" />
                  <line x1="9" y1="8" x2="10" y2="8" />
                  <line x1="9" y1="12" x2="10" y2="12" />
                  <line x1="9" y1="16" x2="10" y2="16" />
                  <line x1="14" y1="8" x2="15" y2="8" />
                  <line x1="14" y1="12" x2="15" y2="12" />
                  <line x1="14" y1="16" x2="15" y2="16" />
                  <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" />
                </svg>
                <h3>Volume licensing for schools</h3>
                <p>
                  Educational institutions can license a class or department at once. Tell us how many seats you need.
                </p>
                <Link className={styles.button} to="/support/">
                  <i>Contact us</i>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <CTA />
      </Layout>
    );
  }
}

export default Page;
